import React, { useMemo } from 'react'
import { makeStyles } from '@mui/styles';
import {Box, Paper, Typography} from '@mui/material';
import DynamicTable from 'components/DynamicTable';
import { getBatchUserAnalytics } from 'services/analyticsV2';
import { exportToCsv, roundToZeroDecimals } from 'utils';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'contexts';
import { CASE_CATEGORIES, isCaseVariant } from './constant';

const useStyle = makeStyles(theme => ({
    borderBottom: {
      borderBottom: "1px solid #8692A3",
      paddingBottom: theme.spacing(1.5),
      width: "100%",
      display : 'flex',
      flexDirection : 'column',
    }
  }))

// Define common filter options
const commonFilterOptions = [
  {
    field: 'finishedAttempts',
    label: 'Finished Attempts',
    options: [
      { label: '0+', value: 0 },
      { label: '5+', value: 5 },
      { label: '10+', value: 10 },
      { label: '20+', value: 20 },
    ],
  },
  {
    field: 'unfinishedAttempts',
    label: 'Unfinished Attempts',
    options: [
      { label: '0+', value: 0 },
      { label: '5+', value: 5 },
      { label: '10+', value: 10 },
      { label: '20+', value: 20 },
    ],
  },
];

const formatNumberOrDash = (value, decimals = 0) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return decimals > 0 ? num.toFixed(decimals) : num.toFixed(0);
};

const calculateCodingAverage = (row) => {
  const codingOverall = row.coding?.overall || 0;
  const coreConceptsOverall = row.coreConcepts?.overall || 0;
  const criticalThinkingOverall = row.criticalThinking?.overall || 0;
  const contentOverall = row.content?.overall || 0;
  return (codingOverall + coreConceptsOverall + criticalThinkingOverall + contentOverall) / 4;
};

const StudentsPerformanceList = ({ interviewVariantType = 'verbal_mock' }) => {
  const classes = useStyle();
  const snackbar = useSnackbar();
  const [rowSelectionModel, setRowSelectionModel] = React.useState([]);
  const [refresh, setRefresh] = React.useState(false);
  const { id: batchId } = useParams();
  const [exporting, setExporting] = React.useState(false);

  const initialFilters = React.useMemo(() => ({
    batchId,
    interviewVariantType
  }), [batchId, interviewVariantType]);

  // Define filters based on interviewVariantType
  const filters = React.useMemo(() => {
    const commonFilters = [
      {
        field: 'name',
        label: 'Student Name',
        type: 'text',
        placeholder: 'Search by name',
      },
      {
        field: 'email',
        label: 'Email',
        type: 'text',
        placeholder: 'Search by email',
      },
      {
        field: 'finishedAttempts',
        label: 'Finished Attempts',
        type: 'number',
        placeholder: 'Min attempts',
      },
      {
        field: 'unfinishedAttempts',
        label: 'Unfinished Attempts',
        type: 'number',
        placeholder: 'Min unfinished',
      },
    ];

    if (isCaseVariant(interviewVariantType)) {
      return [
        ...commonFilters,
        {
          field: 'overallScore',
          label: 'Overall Score',
          type: 'number',
          placeholder: 'Min score (0-10)',
        },
        ...CASE_CATEGORIES.map(({ id, label }) => ({
          field: id,
          label,
          type: 'number',
          placeholder: 'Min score',
        })),
      ];
    }

    if (interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw') {
      return [
        ...commonFilters,
        {
          field: 'overallScore',
          label: 'Overall Score',
          type: 'number',
          placeholder: 'Min overall score',
        },
        {
          field: 'dsaUsageAndUnderstanding',
          label: 'DSA Usage & Understanding',
          type: 'number',
          placeholder: 'Min score',
        },
        {
          field: 'edgeCaseHandling',
          label: 'Edge Case Handling',
          type: 'number',
          placeholder: 'Min score',
        },
        {
          field: 'codeOrganizationAndClarity',
          label: 'Code Organization & Clarity',
          type: 'number',
          placeholder: 'Min score',
        },
        {
          field: 'scalabilityAndEfficiency',
          label: 'Scalability & Efficiency',
          type: 'number',
          placeholder: 'Min score',
        },
        {
          field: 'conceptualClarity',
          label: 'Conceptual Clarity',
          type: 'number',
          placeholder: 'Min score',
        },
        {
          field: 'logicalReasoningAndAnalysis',
          label: 'Logical Reasoning & Analysis',
          type: 'number',
          placeholder: 'Min score',
        },
        {
          field: 'adaptiveImprovement',
          label: 'Adaptive Improvement',
          type: 'number',
          placeholder: 'Min score',
        },
        {
          field: 'explanationAndLogicalCoherence',
          label: 'Explanation & Logical Coherence',
          type: 'number',
          placeholder: 'Min score',
        },
      ];
    } else {
      return [
        ...commonFilters,
        {
          field: 'overallScore',
          label: 'Overall Score',
          type: 'number',
          placeholder: 'Min overall score',
        },
        {
          field: 'overallComm',
          label: 'Avg. Communication',
          type: 'number',
          placeholder: 'Min communication',
        },
        {
          field: 'overallContent',
          label: 'Avg. Content',
          type: 'number',
          placeholder: 'Min content',
        },
      ];
    }
  }, [interviewVariantType]);

  const filterOptions = React.useMemo(() => {
    const baseOptions = [...commonFilterOptions];

    if (isCaseVariant(interviewVariantType)) {
      return [
        ...baseOptions,
        {
          field: 'overallScore',
          label: 'Overall Score',
          options: [
            { label: '0+', value: 0 },
            { label: '4+', value: 4 },
            { label: '6+', value: 6 },
            { label: '8+', value: 8 },
          ],
        },
        ...CASE_CATEGORIES.map(({ id, label }) => ({
          field: id,
          label,
          options: [
            { label: '0+', value: 0 },
            { label: '40+', value: 40 },
            { label: '60+', value: 60 },
            { label: '80+', value: 80 },
          ],
        })),
      ];
    }

    if (interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw') {
      return [
        ...baseOptions,
        {
          field: 'overallScore',
          label: 'Overall Score',
          options: [
            { label: '0+', value: 0 },
            { label: '2+', value: 2 },
            { label: '3+', value: 3 },
            { label: '4+', value: 4 },
          ],
        },
        {
          field: 'dsaUsageAndUnderstanding',
          label: 'DSA Usage & Understanding',
          options: [
            { label: '0+', value: 0 },
            { label: '2+', value: 2 },
            { label: '3+', value: 3 },
            { label: '4+', value: 4 },
          ],
        },
        {
          field: 'edgeCaseHandling',
          label: 'Edge Case Handling',
          options: [
            { label: '0+', value: 0 },
            { label: '2+', value: 2 },
            { label: '3+', value: 3 },
            { label: '4+', value: 4 },
          ],
        },
        {
          field: 'codeOrganizationAndClarity',
          label: 'Code Organization & Clarity',
          options: [
            { label: '0+', value: 0 },
            { label: '2+', value: 2 },
            { label: '3+', value: 3 },
            { label: '4+', value: 4 },
          ],
        },
        {
          field: 'scalabilityAndEfficiency',
          label: 'Scalability & Efficiency',
          options: [
            { label: '0+', value: 0 },
            { label: '2+', value: 2 },
            { label: '3+', value: 3 },
            { label: '4+', value: 4 },
          ],
        },
        {
          field: 'conceptualClarity',
          label: 'Conceptual Clarity',
          options: [
            { label: '0+', value: 0 },
            { label: '2+', value: 2 },
            { label: '3+', value: 3 },
            { label: '4+', value: 4 },
          ],
        },
        {
          field: 'logicalReasoningAndAnalysis',
          label: 'Logical Reasoning & Analysis',
          options: [
            { label: '0+', value: 0 },
            { label: '2+', value: 2 },
            { label: '3+', value: 3 },
            { label: '4+', value: 4 },
          ],
        },
        {
          field: 'adaptiveImprovement',
          label: 'Adaptive Improvement',
          options: [
            { label: '0+', value: 0 },
            { label: '2+', value: 2 },
            { label: '3+', value: 3 },
            { label: '4+', value: 4 },
          ],
        },
        {
          field: 'explanationAndLogicalCoherence',
          label: 'Explanation & Logical Coherence',
          options: [
            { label: '0+', value: 0 },
            { label: '2+', value: 2 },
            { label: '3+', value: 3 },
            { label: '4+', value: 4 },
          ],
        },
      ];
    } else {
      return [
        ...baseOptions,
        {
          field: 'overallScore',
          label: 'Overall Score',
          options: [
            { label: '0+', value: 0 },
            { label: '50+', value: 50 },
            { label: '70+', value: 70 },
            { label: '90+', value: 90 },
          ],
        },
        {
          field: 'overallComm',
          label: 'Avg. Communication',
          options: [
            { label: '0+', value: 0 },
            { label: '50+', value: 50 },
            { label: '70+', value: 70 },
            { label: '90+', value: 90 },
          ],
        },
        {
          field: 'overallContent',
          label: 'Avg. Content',
          options: [
            { label: '0+', value: 0 },
            { label: '20+', value: 20 },
            { label: '40+', value: 40 },
            { label: '60+', value: 60 },
          ],
        },
      ];
    }
  }, [interviewVariantType]);

  const isCodingVariant = useMemo(
    () => interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw',
    [interviewVariantType]
  );

  const isCase = useMemo(() => isCaseVariant(interviewVariantType), [interviewVariantType]);

  // Case scores are 0-10 overall and 0-100 per category, so the overall column
  // keeps two decimals while category columns round to whole numbers.
  const caseColumns = useMemo(() => [
    {
      field: 'name',
      label: 'Student name',
      minWidth: 180,
      flex: 1,
      disableSorting: true,
      renderCell: (params) => params.row.name || '-',
    },
    {
      field: 'email',
      label: 'Email',
      minWidth: 200,
      flex: 1,
      disableSorting: true,
      renderCell: (params) => params.row.email || '-',
    },
    {
      field: 'overallScore',
      label: 'Overall Score',
      minWidth: 130,
      align: 'center',
      fixed: true,
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.overallScore, 2),
    },
    ...CASE_CATEGORIES.map(({ id, label }) => ({
      field: id,
      label,
      minWidth: 170,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row[id], 0),
    })),
    {
      field: 'highestScore',
      label: 'Highest Score',
      minWidth: 130,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.highestScore, 2),
    },
    {
      field: 'lowestScore',
      label: 'Lowest Score',
      minWidth: 130,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.lowestScore, 2),
    },
    {
      field: 'finishedAttempts',
      label: 'Finished Attempts',
      minWidth: 150,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.finishedAttempts, 0),
    },
    {
      field: 'unfinishedAttempts',
      label: 'Unfinished Attempts',
      minWidth: 160,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.unfinishedAttempts, 0),
    },
    {
      field: 'droppedMidSession',
      label: 'Dropped Mid-session',
      minWidth: 170,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.droppedMidSession, 0),
    },
    {
      field: 'proctoredIssues',
      label: 'Proctored Issues',
      minWidth: 150,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.proctoredIssues, 0),
    },
    {
      field: 'averageTimeTaken',
      label: 'Avg. Time Taken (min)',
      minWidth: 170,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.averageTimeTaken, 1),
    },
    {
      field: 'casesAttempted',
      label: 'Cases Attempted',
      minWidth: 150,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.casesAttempted, 0),
    },
    {
      field: 'casesAvailable',
      label: 'Cases Available',
      minWidth: 150,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.casesAvailable, 0),
    },
    {
      field: 'mostRecentAttemptAt',
      label: 'Most Recent Attempt',
      minWidth: 180,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => {
        const v = params.row.mostRecentAttemptAt;
        if (!v) return '-';
        try { return new Date(v).toLocaleString(); } catch { return '-'; }
      },
    },
  ], []);

  // Define coding columns for coding_mock and coding_iw
  const codingColumns = useMemo(() => [
    {
      field: 'name',
      label: 'Student name',
      minWidth: 180,
      flex: 1,
      renderCell: (params) => params.row.name || '-',
      disableSorting: true,
    },
    {
      field: 'email',
      label: 'Email',
      minWidth: 200,
      flex: 1,
      renderCell: (params) => params.row.email || '-',
      disableSorting: true,
    },
    {
      field: 'overallScore',
      label: 'Overall Score',
      minWidth: 120,
      align: 'center',
      disableExport: true,
      fixed: true,
      renderCell: (params) => formatNumberOrDash(params.row.overallScore, 0),
      disableSorting: true,
    },
    {
      field: 'coding',
      label: 'Coding',
      minWidth: 100,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.coding, 0),
    },
    {
      field: 'coreConcepts',
      label: 'Core Concepts',
      minWidth: 140,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.coreConcepts, 0),
    },
    {
      field: 'criticalThinking',
      label: 'Critical Thinking',
      minWidth: 150,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.criticalThinking, 0),
    },
    {
      field: 'content',
      label: 'Content',
      minWidth: 110,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.content, 0),
    },
    {
      field: 'speech',
      label: 'Speech',
      minWidth: 110,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.speech, 0),
    },
    {
      field: 'dsaUsageAndUnderstanding',
      label: 'DSA Usage & Understanding',
      minWidth: 100,
      align: 'center',
      disableExport: true,
      renderCell: (params) => formatNumberOrDash(params.row.dsaUsageAndUnderstanding, 0),
      disableSorting: true,
      disableExport: true,
    },
    {
      field: 'edgeCaseHandling',
      label: 'Edge Case Handling',
      minWidth: 160,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.edgeCaseHandling, 0),
    },
    {
      field: 'codeOrganizationAndClarity',
      label: 'Code Organization & Clarity',
      minWidth: 200,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.codeOrganizationAndClarity, 0),
    },
    {
      field: 'scalabilityAndEfficiency',
      label: 'Scalability & Efficiency',
      minWidth: 180,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.scalabilityAndEfficiency, 0),
    },
    {
      field: 'conceptualClarity',
      label: 'Conceptual Clarity',
      minWidth: 160,
      align: 'center',
      renderCell: (params) => formatNumberOrDash(params.row.conceptualClarity, 0),
      disableSorting: true,
    },
    {
      field: 'logicalReasoningAndAnalysis',
      label: 'Logical Reasoning & Analysis',
      minWidth: 220,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.logicalReasoningAndAnalysis, 0),
    },
    {
      field: 'adaptiveImprovement',
      label: 'Adaptive Improvement',
      minWidth: 180,
      align: 'center',
      renderCell: (params) => formatNumberOrDash(params.row.adaptiveImprovement, 0),
      disableSorting: true,

      },
    {
      field: 'explanationAndLogicalCoherence',
      label: 'Explanation & Logical Coherence',
      minWidth: 240,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.explanationAndLogicalCoherence, 0),
    },
    ...(interviewVariantType === 'coding_iw' ? [
      {
        field: 'fluency',
        label: 'Fluency',
        minWidth: 120,
        align: 'center',
        disableSorting: true,
        renderCell: (params) => formatNumberOrDash(params.row.fluency, 0),
      },
      {
        field: 'delivery',
        label: 'Delivery',
        minWidth: 120,
      align: 'center',
      disableSorting: true,
        renderCell: (params) => formatNumberOrDash(params.row.delivery, 0),
      },
    ] : []),
    {
      field: 'finishedAttempts',
      label: 'Finished Attempts',
      minWidth: 140,
      disableSorting: true,
      align: 'center',
      renderCell: (params) => formatNumberOrDash(params.row.finishedAttempts, 0),
    },
    {
      field: 'unfinishedAttempts',
      label: 'Unfinished Attempts',
      minWidth: 140,
      disableSorting: true,
      align: 'center',
      renderCell: (params) => formatNumberOrDash(params.row.unfinishedAttempts, 0),
    },
    {
      field: 'averageTimeTaken',
      label: 'Avg. Time Taken (min)',
      minWidth: 160,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => {
        const v = params.row.averageTimeTaken;
        if (v == null || v === '' || isNaN(parseFloat(v))) return '-';
        return Number(v).toFixed(1);
      },
    },
    {
      field: 'averageViolationCount',
      label: 'Avg. Violations',
      minWidth: 140,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.averageViolationCount, 0),
    },
    {
      field: 'averageHintUsed',
      label: 'Avg. Hints Used',
      minWidth: 140,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.averageHintUsed, 0),
    },
    {
      field: 'uniqueChallengeAttempted',
      label: 'Unique Challenges Attempted',
      minWidth: 200,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.uniqueChallengeAttempted, 0),
    },
    {
      field: 'mostRecentAttemptAt',
      label: 'Most Recent Attempt',
      minWidth: 180,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => {
        const v = params.row.mostRecentAttemptAt;
        if (!v) return '-';
        try { return new Date(v).toLocaleString(); } catch { return '-'; }
      },
    },
  ], [interviewVariantType]);

  // Define verbal columns for verbal_mock and verbal_iw
  const verbalColumns = useMemo(() => [
    {
      field: 'name',
      label: 'Student name',
      minWidth: 180,
      flex: 1,
      disableSorting: true,
      valueGetter: row => row.name || '-',
    },
    {
      field: 'email',
      label: 'Email',
      minWidth: 200,
      flex: 1,
      disableSorting: true,
      valueGetter: row => row.email || '-',
    },
    {
      field: 'overallScore',
      label: 'Overall Score',
      minWidth: 120,
      align: 'center',
      fixed: true,
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.overallScore, 0),
    },
    {
      field: 'overallComm',
      label: 'Avg. Communication',
      minWidth: 140,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.overallComm, 0),
    },
    {
      field: 'overallContent',
      label: 'Avg. Content',
      minWidth: 120,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.overallContent, 0),
    },
    {
      field: 'highestCommScore',
      label: 'Highest Comm.',
      minWidth: 120,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.highestCommScore, 0),
    },
    {
      field: 'lowestCommScore',
      label: 'Lowest Comm.',
      minWidth: 120,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.lowestCommScore, 0),
    },
    {
      field: 'highestContentScore',
      label: 'Highest Content',
      minWidth: 120,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.highestContentScore, 0),
    },
    {
      field: 'lowestContentScore',
      label: 'Lowest Content',
      minWidth: 120,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.lowestContentScore, 0),
    },
    {
      field: 'finishedAttempts',
      label: 'Finished Attempts',
      minWidth: 120,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.finishedAttempts, 0),
    },
    {
      field: 'unfinishedAttempts',
      label: 'Unfinished Attempts',
      minWidth: 120,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.unfinishedAttempts, 0),
    },
    {
      field: 'averageTimeTaken',
      label: 'Avg. Time Taken (min)',
      minWidth: 140,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => {
        const v = params.row.averageTimeTaken;
        if (v == null || v === '' || isNaN(parseFloat(v))) return '-';
        return Number(v).toFixed(1);
      },
    },
    {
      field: 'assessmentAttempted',
      label: 'Assessments Attempted',
      minWidth: 170,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.assessmentAttempted, 0),
    },
    {
      field: 'assessmentAvailable',
      label: 'Assessments Available',
      minWidth: 170,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => formatNumberOrDash(params.row.assessmentAvailable, 0),
    },
    {
      field: 'averageQuestionAnswered',
      label: 'Avg. Qs Answered',
      minWidth: 140,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => {
        const v = params.row.averageQuestionAnswered;
        if (v == null || v === '' || isNaN(parseFloat(v))) return '-';
        return Number(v).toFixed(1);
      },
    },
    {
      field: 'mostRecentAttemptAt',
      label: 'Most Recent Attempt',
      minWidth: 180,
      align: 'center',
      disableSorting: true,
      renderCell: (params) => {
        const v = params.row.mostRecentAttemptAt;
        if (!v) return '-';
        try { return new Date(v).toLocaleString(); } catch { return '-'; }
      },
    },
  ], []);

  // Choose columns based on interviewVariantType
  const columns = useMemo(
    () => (isCase ? caseColumns : isCodingVariant ? codingColumns : verbalColumns),
    [isCase, isCodingVariant, caseColumns, codingColumns, verbalColumns]
  );

  const handleExport = async (selectedColumns) => {
    try {
      setExporting(true);
      const exportData = await getBatchUserAnalytics({
        batchId,
        interviewVariantType,
        toExport: true
      });
      const items = exportData?.performance;
      if (items && Array.isArray(items)) {
        const columnFields = selectedColumns.map(col => col.field || col.id);
        exportToCsv(selectedColumns, items, columnFields, 'students-performance');
      }
    } catch (error) {
      console.error('Export failed:', error);
      snackbar.error(error.message, "error");
    } finally {
      setExporting(false);
      snackbar.success("Successfully exported the data!")
    }
  };

  return (
    <Box sx={{backgroundColor : '#FFFFFF' , marginTop : '16px'}}>
    <Box className={classes.borderBottom}>
      <Typography variant="h4-medium">
        Students performance
      </Typography>
      <Typography variant="body01-bold" color='neutral.clr-600'>
        View students' performance in this batch
      </Typography>
    </Box>
    <Paper elevation={0} style={{ width: "100%" , marginTop : '12px' }}>
      <DynamicTable
        key={1}
        hideDeleteAction
        entity="Draft"
        noRowsMessage={'No students found'} 
        columns={columns}
        filters={filters}
        filterOptions={filterOptions}
        sx={{ p: 0 }}
        useListProps={{
          getItems: getBatchUserAnalytics,
          itemName: 'performance',
          initialFilters,
        }}
        rowSelectionModel={rowSelectionModel}
        setRowSelectionModel={setRowSelectionModel}
        personalizedList={true}
        refreshData={refresh}
        setRefresh={setRefresh}     
        hideColumnSelection={false}   
        onExport={handleExport}
        pagination={false}
        loading={exporting}
      />
    </Paper>  
  </Box>
  )
}

export default StudentsPerformanceList